import { filter, reduce } from "lodash";
import { useCallback, useState } from "react";
import { change, getFormValues } from "redux-form";

import { AddNoteFormValues, ADD_NOTE_FORM_NAME } from "../modals/AddNoteForm";
import { BULK_ITEM_CATEGORY_OTHER_ID } from "src/constants";
import { BulkyItem, SchedulerWizardFormValues } from "src/interfaces";
import {
  IconButton,
  IconButtonIcon,
  ItemSelectionRowWrapper,
  TextDisplay,
  Wrapper,
} from "src/styled";
import { Input, NumberInput, TypedField, confirm } from "src/components";
import { isRequired } from "src/utils/validators";
import { translate } from "src/services";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "./AddressForm";
import AddNoteModal from "../modals/AddNoteModal";

interface Props {
  item: BulkyItem;
}

const ItemSelectionQuantityRow = ({ item }: Props) => {
  const {
    categoryLabelToDisplay,
    itemLabelToDisplay,
    bulkyCategoryTypeId,
    bulkyItemTypeId,
    name,
    note,
  } = item;

  const dispatch = useAppDispatch();
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const handleAddNote = useCallback(
    (v: AddNoteFormValues) => {
      dispatch(change(WIZARD_FORM_NAME, `${name}.note`, v.note));
      setIsAddNoteModalOpen(false);
    },
    [dispatch, name]
  );

  const handleOpenNoteModal = async () => {
    await setIsAddNoteModalOpen(true);
    dispatch(change(ADD_NOTE_FORM_NAME, "note", item.note));
  };

  const handleQuantityChange = useCallback(
    async (v: any) => {
      if (v === 0) {
        if (await confirm(translate("wizardPage.confirmRemoveItem"))) {
          const newItems = reduce(
            thisFormValues.items,
            (acc, item) => {
              if (item.name !== name) {
                const index = item.name?.split(".")[1];
                if (index) acc[index] = item;
              }
              return acc;
            },
            {} as any
          );

          if (
            bulkyItemTypeId &&
            (thisFormValues.itemsMultiselect?.includes(bulkyItemTypeId.toString()) ||
              bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID)
          )
            dispatch(
              change(
                WIZARD_FORM_NAME,
                "itemsMultiselect",
                filter(
                  thisFormValues.itemsMultiselect,
                  (item) => item !== bulkyItemTypeId.toString()
                )
              )
            );
          dispatch(change(WIZARD_FORM_NAME, "items", newItems));
        } else {
          dispatch(change(WIZARD_FORM_NAME, `${name}.quantity`, 1));
        }
      }
    },
    [
      bulkyCategoryTypeId,
      bulkyItemTypeId,
      dispatch,
      name,
      thisFormValues.items,
      thisFormValues.itemsMultiselect,
    ]
  );

  return (
    <>
      <ItemSelectionRowWrapper hasNote={!!item.note}>
        <Wrapper>
          <TextDisplay size="xxSmall" color="grayDarker" block margin="no no xxSmall">
            {categoryLabelToDisplay}
          </TextDisplay>
          {bulkyItemTypeId && (
            <TextDisplay size="small" block>
              {itemLabelToDisplay}
            </TextDisplay>
          )}

          {bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID && !bulkyItemTypeId && (
            <TypedField
              component={Input}
              name={`${name}.customBulkyItemName`}
              props={{
                margin: "no",
                type: "text",
                placeholder: translate("wizardPage.pleaseTypeYourItem"),
                noBottomBorder: true,
                width: "200px",
              }}
              validate={[isRequired]}
            />
          )}
        </Wrapper>
        <Wrapper display="flex" justifyContent="flex-end" alignItems="center">
          <IconButton
            type="button"
            color={item.note ? "primary" : ""}
            onClick={handleOpenNoteModal}
          >
            <IconButtonIcon
              size="large"
              icon="addNote"
              customViewBox="0 0 11 14"
              color={item.note ? "primary" : ""}
            />
            {item.note ? translate("wizardPage.editNote") : translate("wizardPage.addNote")}
          </IconButton>
          <TypedField
            component={NumberInput}
            onChange={(v) => handleQuantityChange(v)}
            onFocus={(e) => e.preventDefault()}
            name={`${name}.quantity`}
            props={{
              margin: "no no no small",
              min: 0,
              max: 10,
            }}
          />
        </Wrapper>
      </ItemSelectionRowWrapper>

      {item.note && (
        <ItemSelectionRowWrapper>
          <Wrapper>
            <TextDisplay size="xSmall" color="grayDarker" block margin="no no xxSmall">
              {item.note}
            </TextDisplay>
          </Wrapper>
        </ItemSelectionRowWrapper>
      )}

      {isAddNoteModalOpen && (
        <AddNoteModal
          closeModal={() => setIsAddNoteModalOpen(false)}
          initialValues={{ note: note }}
          title={itemLabelToDisplay || categoryLabelToDisplay || ""}
          onSubmit={handleAddNote}
        />
      )}
    </>
  );
};

export default ItemSelectionQuantityRow;
